import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Type,
    Output,
    EventEmitter,
    HostListener,
    OnInit,
    AfterViewInit,
    HostBinding,
} from '@angular/core';
import { Result } from '@team23/ts-result';
import { ApiDataStatus } from '@ui/shared/util-api';
import { ListItem, ListItemComponent } from '@ui/shared/util-list';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ui-list-view',
    templateUrl: './list-view.component.html',
    styleUrls: ['./list-view.component.scss'],
    standalone: false,
})
export class ListViewComponent<ListItemType extends ListItem> implements OnInit, AfterViewInit {
    @Input({ required: true })
    public itemContentComponentType!: Type<ListItemComponent<ListItemType>>;

    @Input({ required: true })
    public listItems!: Result<Array<ListItemType>, ApiDataStatus>;

    @Input()
    public areFiltersExpanded = false;

    @Output()
    public readonly reloadListData = new EventEmitter<void>();

    @HostBinding('class.fullscreen')
    public isFullscreen = false;

    public loadingStatus = ApiDataStatus.Loading;

    public get isListDataErrored(): boolean {
        return !!(this.listItems.error && this.listItems.error.key !== ApiDataStatus.Loading);
    }

    @HostListener('window:resize')
    public onResize(): void {
        // Set the fullscreen viewport height whenever the window resizes.
        this._setFullscreenHeight();
    }

    @HostListener('document:fullscreenchange')
    public onFullscreenChange(): void {
        this.isFullscreen = !!document.fullscreenElement;
    }

    public triggerListDataReload(): void {
        this.reloadListData.emit();
    }

    public ngOnInit(): void {
        this.onResize();
    }

    public ngAfterViewInit(): void {
        this.onResize();
    }

    /**
     * Sets the ideal view height for certain page elements in fullscreen.
     * The smaller value of `screen.height` and `window.innerheight` should be safe for most devices.
     *
     * This is intended to prevent overlaps and cutoffs in fullscreen on mobile, where the available space is unreliable.
     */
    private _setFullscreenHeight(): void {
        const htmlElement = document.querySelector<HTMLElement>('#list-view');

        const availableHeights = [
            screen.height,
            window.innerHeight,
        ]
            .filter(height => height > 0);

        if (availableHeights.length) {
            const safeHeight = Math.min(...availableHeights);

            if (htmlElement) {
                htmlElement.style.setProperty('--fullscreen-safe-height', `${ safeHeight }px`);
            }
        }
    }
}
